import React from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { CraftFormProvider, FormRenderer } from "@craft-form/renderer";
import useCraftDataProvider from "../Hook/useCraftDataProvider";
import useSubmitProposalForm from "../Hook/useSubmitProposalForm";
import LoadingSpinner from "../../../../Components/Common/LoadingSpinner/LoadingSpinner";
import craftFormTheme from "../../../../Utils/craftFormTheme";

// `methods` is owned by ProposalModal (not created here) so it can read this
// form's live values for "Save Draft" — @craft-form/renderer's `methods`
// prop exists precisely to share one react-hook-form instance with a host.
const ProposalForm = ({
  isEditing = false,
  methods,
  isLoading,
  isError,
  rendererMode,
}) => {
  const { id } = useParams();
  const detailsData = useSelector(
    (state) => state.order.selectedOrders.detailsData,
  );
  const FIELDS = useSelector((state) => state.order.ProposalFieldsJSON);
  const LAYOUT = useSelector((state) => state.order.ProposalLayoutJSON);
  const RULES_JSON = useSelector((state) => state.order.ProposalRulesJSON);

  const dataProvider = useCraftDataProvider(detailsData?.ag_id);
  const submitProposalForm = useSubmitProposalForm(id, detailsData);
  const isViewMode = rendererMode === "view";
  const layout = LAYOUT?.form?.children ?? LAYOUT;

  // ProposalFieldsJSON/ProposalLayoutJSON/ProposalRulesJSON are populated
  // asynchronously by getFormRenderer (OrderService.js), fired from the
  // order details page independently of when this modal opens — their
  // redux defaults ({}, [], []) don't match the shapes FormRenderer needs,
  // so render nothing until getFormRenderer has actually resolved.
  if (isLoading || !Array.isArray(FIELDS) || !Array.isArray(layout)) {
    return <LoadingSpinner title="Loading proposal form..." />;
  }

  if (isError) {
    return <p className="text-danger">Failed to load proposal form.</p>;
  }

  return (
    <CraftFormProvider
      config={{
        mode: "live",
        rulesEnabled: true,
        validationsEnabled: true,
        ...(isEditing && {
          requiredValidationEnabled: false,
        }),
        ...(isViewMode && {
          groupedLayoutViewMode: "field-set",
        }),
      }}
      dataProvider={dataProvider}
      methods={methods}
      theme={craftFormTheme}
    >
      <FormRenderer
        fields={FIELDS}
        layout={layout}
        rulesJson={RULES_JSON}
        // fitContainer forces its own overflow:hidden root for the edit
        // wizard's paginated steps; view mode has no steps, so let it flow
        // and scroll via .proposal-view-fit-container instead.
        fitContainer={!isViewMode}
        onSave={(values) => submitProposalForm.mutate(values)}
        mode={rendererMode || (isEditing ? "update" : "create")}
      />
    </CraftFormProvider>
  );
};

export default ProposalForm;
